import React, { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  /**
   * Whether the body scrolls as a whole. Turn off when the content manages its
   * own scrolling region, so only that region moves.
   */
  scrollBody?: boolean;
};

/**
 * Shared dialog shell: overlay, centred panel, header with close button,
 * scrollable body and an optional footer. Radix handles focus trapping,
 * Escape and scroll locking — the things react-bootstrap's Modal was doing.
 */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className = "max-w-lg",
  scrollBody = true,
}: Props) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="animate-overlay-in fixed inset-0 z-40 bg-zinc-900/40 backdrop-blur-sm" />
        <Dialog.Content
          className={`animate-dialog-in bg-surface border-hairline/70 fixed top-1/2 left-1/2 z-50 flex max-h-[90dvh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border shadow-2xl shadow-zinc-900/10 ${className}`}
        >
          <header className="border-hairline/60 flex items-start justify-between gap-4 border-b px-6 py-5">
            <div>
              <Dialog.Title className="text-base font-semibold text-zinc-900">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-sm text-zinc-500">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              aria-label="Close"
              className="hover:bg-elevated -mr-2 rounded-lg p-2 text-zinc-500 transition-colors hover:text-zinc-900"
            >
              <i className="bi bi-x-lg text-sm" />
            </Dialog.Close>
          </header>

          <div
            className={`flex-1 px-6 py-5 ${
              scrollBody
                ? "overflow-y-auto"
                : "flex min-h-0 flex-col overflow-hidden"
            }`}
          >
            {children}
          </div>

          {footer && (
            <footer className="border-hairline/60 bg-canvas/60 flex items-center justify-end gap-3 border-t px-6 py-4">
              {footer}
            </footer>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

type DrawerProps = Omit<Props, "className" | "scrollBody"> & {
  side?: "left" | "right";
  className?: string;
  /** Escape hatch for a caller-supplied width. */
  style?: React.CSSProperties;
};

/**
 * Off-canvas panel — the replacement for react-bootstrap's Offcanvas, which
 * admin uses 24 times. Same Radix dialog underneath, slid in from an edge.
 */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  side = "right",
  className = "w-full max-w-md",
  style,
}: DrawerProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="animate-overlay-in fixed inset-0 z-40 bg-zinc-900/40 backdrop-blur-sm" />
        <Dialog.Content
          style={style}
          className={`animate-drawer-in bg-surface border-hairline/70 fixed inset-y-0 z-50 flex flex-col shadow-2xl shadow-zinc-900/10 ${
            side === "right" ? "right-0 border-l" : "left-0 border-r"
          } ${className}`}
        >
          <header className="border-hairline/60 flex items-start justify-between gap-4 border-b px-6 py-5">
            <div>
              <Dialog.Title className="text-base font-semibold text-zinc-900">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-sm text-zinc-500">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              aria-label="Close"
              className="hover:bg-elevated -mr-2 rounded-lg p-2 text-zinc-500 transition-colors hover:text-zinc-900"
            >
              <i className="bi bi-x-lg text-sm" />
            </Dialog.Close>
          </header>

          <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

          {footer && (
            <footer className="border-hairline/60 bg-canvas/60 flex items-center justify-end gap-3 border-t px-6 py-4">
              {footer}
            </footer>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
